// A take's video with a band under it (see BandForTake.js). The take's own sound and the band play
// together from decoded audio, on one clock, so they stay sample for sample where they were lined
// up, and the picture is kept to that clock. The same plays again into a recorder to write out one
// video: the take's picture and sound, with the band. Browser only.
import { wavFile } from "../../lib/audio-search/browser";

const AudioContextClass = () => window.AudioContext || window.webkitAudioContext;
const mono = (buffer) => {
  const samples = new Float32Array(buffer.length);
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) { const data = buffer.getChannelData(channel); for (let i = 0; i < data.length; i++) samples[i] += data[i] / buffer.numberOfChannels; }
  return samples;
};
async function decode(data, failure) {
  const context = new (AudioContextClass())();
  try { const buffer = await context.decodeAudioData(data); return { buffer, samples: mono(buffer), rate: buffer.sampleRate }; }
  catch { throw new Error(failure); }
  finally { context.close?.().catch(() => {}); }
}
// A take's sound, as recorded (for the mix) and as mono samples (to hear where its beat is).
export const readTake = async (blob) => decode(await blob.arrayBuffer(), "This video's sound couldn't be read.");
// A band the studio made, the same way.
export async function readBand(url) {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) throw new Error("The band couldn't be loaded. Please try again.");
  return decode(await response.arrayBuffer(), "The band couldn't be read.");
}

// What the studio hears: the take from bar 1, up to `seconds`, mono at 22 kHz, as a WAV.
export async function studioTake(buffer, from, seconds) {
  const rate = 22050, length = Math.max(rate, Math.round(Math.min(seconds, buffer.duration - from) * rate));
  const offline = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, length, rate), source = offline.createBufferSource();
  source.buffer = buffer; source.connect(offline.destination); source.start(0, Math.max(0, from));
  return wavFile((await offline.startRendering()).getChannelData(0), rate, "take.wav");
}

// The take (`take`, an AudioBuffer of its sound) with a band, on `video` (the take's picture; its own
// sound stays muted, since the take's sound plays from `take`):
//   setBand(buffer, at)   a band, starting `at` seconds into the take (null for none)
//   setLevel(value)       the band's level (1 as made)
//   play(from) / pause()  from a time in the take; `onTime(seconds)` and `onEnd()` follow it
//   render({ formats, onProgress })  the whole take with the band, recorded as one video: { blob, extension }
export function createTakeMix({ video, take, onTime = () => {}, onEnd = () => {} }) {
  const context = new (AudioContextClass())(), master = context.createDynamicsCompressor(), takeGain = context.createGain(), bandGain = context.createGain();
  // A gentle limiter: the take and the band together mustn't clip.
  master.threshold.value = -4; master.knee.value = 4; master.ratio.value = 10; master.attack.value = 0.003; master.release.value = 0.15;
  takeGain.connect(master); bandGain.connect(master); master.connect(context.destination);
  video.muted = true;
  let band = null, at = 0, playing = null, frame = 0, position = 0, ended = null;
  const now = () => (playing ? Math.min(take.duration, playing.from + Math.max(0, context.currentTime - playing.start)) : position);

  function stop() {
    cancelAnimationFrame(frame);
    if (!playing) return;
    position = now();
    for (const source of playing.sources) { source.onended = null; try { source.stop(); } catch { /* It had stopped. */ } }
    playing = null;
    video.pause();
  }
  function play(from = position, { live = true } = {}) {
    stop();
    context.resume().catch(() => {});
    from = Math.max(0, Math.min(from, take.duration - 0.05));
    const start = context.currentTime + 0.15, lick = context.createBufferSource(), sources = [lick];
    lick.buffer = take; lick.connect(takeGain); lick.start(start, from);
    if (band && at + band.duration > from) {
      const source = context.createBufferSource(), fade = context.createGain(), into = from - at, when = start + Math.max(0, -into), end = start + Math.min(take.duration, at + band.duration) - from;
      source.buffer = band; source.connect(fade).connect(bandGain); source.start(when, Math.max(0, into)); source.stop(end + 0.05);
      // In with the take's bar 1 (or straight away, from part way through); out over the last
      // second and a half, by the take's end or the band's.
      fade.gain.setValueAtTime(into > 0.02 ? 1 : 0, when);
      if (into <= 0.02) fade.gain.linearRampToValueAtTime(1, when + 0.02);
      const out = Math.max(when + 0.03, end - 1.5);
      fade.gain.setValueAtTime(1, out); fade.gain.linearRampToValueAtTime(0, end);
      sources.push(source);
    }
    lick.onended = () => { if (playing?.sources[0] !== lick) return; position = take.duration; playing = null; cancelAnimationFrame(frame); video.pause(); onTime(position); ended?.(); onEnd(); };
    playing = { start, from, sources };
    video.currentTime = from; video.playbackRate = 1;
    setTimeout(() => { if (playing?.sources[0] === lick) video.play().catch(() => {}); }, Math.max(0, (start - context.currentTime) * 1000 - 20));
    // The picture follows the sound: nudged a little faster or slower, or moved when far off. Live,
    // it waits for what the speakers are still to play.
    const delay = live ? context.outputLatency || context.baseLatency || 0 : 0;
    const follow = () => {
      if (!playing) return;
      const lag = video.currentTime - (now() - delay);
      if (!video.paused) { if (Math.abs(lag) > 0.3) video.currentTime = Math.max(0, now() - delay); else video.playbackRate = Math.max(0.9, Math.min(1.1, 1 - lag * 1.5)); }
      onTime(now());
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
  }

  return {
    setBand(buffer, start = 0) { const wasPlaying = Boolean(playing), from = now(); band = buffer; at = start; if (wasPlaying) play(from); },
    setLevel(value) { bandGain.gain.setTargetAtTime(value, context.currentTime, 0.02); },
    play,
    pause: stop,
    seek(seconds) { const wasPlaying = Boolean(playing); stop(); position = Math.max(0, Math.min(take.duration, seconds)); video.currentTime = position; onTime(position); if (wasPlaying) play(position); },
    get playing() { return Boolean(playing); },
    // The whole take again, into a recorder, with nothing heard meanwhile: the picture drawn onto a
    // canvas frame by frame, the sound from the mix.
    async render({ formats, onProgress = () => {} }) {
      context.resume().catch(() => {});
      stop();
      const width = video.videoWidth, height = video.videoHeight;
      if (!width || !height) throw new Error("The video isn't ready yet. Try again in a moment.");
      const canvas = document.createElement("canvas"), drawing = canvas.getContext("2d"), destination = context.createMediaStreamDestination();
      canvas.width = width; canvas.height = height;
      video.currentTime = 0;
      await new Promise((resolve) => { if (video.readyState >= 2 && video.currentTime === 0) resolve(); else video.addEventListener("seeked", resolve, { once: true }); setTimeout(resolve, 1500); });
      drawing.drawImage(video, 0, 0, width, height);
      master.disconnect(); master.connect(destination);
      const stream = new MediaStream([...canvas.captureStream(30).getVideoTracks(), ...destination.stream.getAudioTracks()]);
      const [mimeType] = formats.find(([type]) => MediaRecorder.isTypeSupported(type)) || [""];
      const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 6_000_000, audioBitsPerSecond: 192_000 }), chunks = [];
      recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
      const stopped = new Promise((resolve) => { recorder.onstop = resolve; });
      let drawingOn = true;
      const draw = () => {
        if (!drawingOn) return;
        drawing.drawImage(video, 0, 0, width, height);
        onProgress(Math.min(1, now() / take.duration));
        if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(draw); else requestAnimationFrame(draw);
      };
      try {
        recorder.start(1000);
        const done = new Promise((resolve) => { ended = resolve; });
        play(0, { live: false });
        draw();
        await done;
        // The last frames, then stop.
        await new Promise((resolve) => setTimeout(resolve, 200));
      } finally {
        drawingOn = false; ended = null;
        if (recorder.state !== "inactive") recorder.stop();
        await stopped;
        stream.getTracks().forEach((track) => track.stop());
        master.disconnect(); master.connect(context.destination);
      }
      const type = (recorder.mimeType || mimeType || "video/webm").split(";")[0];
      return { blob: new Blob(chunks, { type }), extension: type.includes("mp4") ? "mp4" : "webm" };
    },
    close() { stop(); context.close?.().catch(() => {}); },
  };
}
